import { useEffect, useState } from 'react'
import { Modal } from '@/components/ui/Modal'
import { Button } from '@/components/ui/Button'
import { TextField } from '@/components/ui/Field'
import { useToast } from '@/components/ui/Toast'
import { useSetMentorRatingOverride } from '@/lib/queries'
import { ApiError } from '@/lib/api'
import { formatRating } from '@/lib/format'
import type { Mentor } from '@/types/api'
import styles from './RatingOverrideDialog.module.css'

interface Props {
  mentor: Mentor | null
  onClose: () => void
}

/**
 * Publish a rating other than the one students gave.
 *
 * *** THE REAL AVERAGE IS NEVER OVERWRITTEN ***
 * The server keeps `rating_avg` as feedback computed it and stores the
 * published figure separately, so this is reversible and the honest number
 * survives. That is why the dialog shows both, and why clearing is a first
 * class action rather than "set it back to roughly what it was".
 */
export function RatingOverrideDialog({ mentor, onClose }: Props) {
  const toast = useToast()
  const mutation = useSetMentorRatingOverride()

  const [rating, setRating] = useState('')
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!mentor) return
    setRating(mentor.rating_is_overridden ? (mentor.rating_avg ?? '') : '')
    setReason(mentor.rating_override_reason ?? '')
    setError(null)
  }, [mentor])

  if (!mentor) return null

  const trueAverage = formatRating(mentor.rating_true_avg) || '—'

  const submit = async (clear: boolean) => {
    setError(null)

    if (!clear) {
      const value = Number(rating)
      if (rating.trim() === '' || Number.isNaN(value) || value < 0 || value > 5) {
        setError('Enter a rating between 0 and 5.')
        return
      }
      if (!reason.trim()) {
        // The server enforces this too, and so does the database. Asking here
        // saves a round trip and explains why it matters.
        setError('Give a reason — it is recorded so this decision can be traced later.')
        return
      }
    }

    try {
      await mutation.mutateAsync({
        id: mentor.user_id,
        rating: clear ? null : Number(rating).toFixed(2),
        reason: clear ? null : reason.trim(),
      })
      toast.success(
        clear
          ? `${mentor.full_name} now shows their real rating again.`
          : `${mentor.full_name} now publishes ${Number(rating).toFixed(2)}.`,
      )
      onClose()
    } catch (caught) {
      setError(
        caught instanceof ApiError ? caught.message : 'Could not save the override.',
      )
    }
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Adjust published rating"
      description={mentor.full_name}
      size="sm"
      footer={
        <div className={styles.footer}>
          {mentor.rating_is_overridden && (
            <Button
              variant="secondary"
              onClick={() => submit(true)}
              loading={mutation.isPending}
            >
              Use real rating
            </Button>
          )}
          <div className={styles.footerRight}>
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => submit(false)}
              loading={mutation.isPending}
            >
              Publish
            </Button>
          </div>
        </div>
      }
    >
      <div className={styles.body}>
        {/*
          Both numbers, side by side. An admin adjusting a score needs to see
          what they are departing from — otherwise they are tuning a figure
          they cannot read.
        */}
        <div className={styles.numbers}>
          <div className={styles.stat}>
            <span className={styles.statLabel}>From students</span>
            <span className={styles.statValue}>{trueAverage}</span>
            <span className={styles.statMeta}>
              {mentor.rating_count} {mentor.rating_count === 1 ? 'rating' : 'ratings'}
            </span>
          </div>
          <div className={styles.stat}>
            <span className={styles.statLabel}>Published now</span>
            <span className={styles.statValue}>
              {formatRating(mentor.rating_avg) || '—'}
            </span>
            <span className={styles.statMeta}>
              {mentor.rating_is_overridden ? 'overridden' : 'the real average'}
            </span>
          </div>
        </div>

        <TextField
          label="Publish this rating instead"
          type="number"
          min={0}
          max={5}
          step={0.01}
          value={rating}
          onChange={(event) => setRating(event.target.value)}
          hint="0 to 5. Students and mentors see this figure; the real average is kept."
        />

        <TextField
          label="Reason"
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          maxLength={500}
          hint="Recorded against your account. An unexplained override becomes folklore."
        />

        {error && <p className={styles.error}>{error}</p>}
      </div>
    </Modal>
  )
}
